import { Column } from "../../../../core/components/Column";
import { Container } from "../../../../core/components/Container";
import { CustomButton } from "../../../../core/components/CustomButton";
import { TextComponent } from "../../../../core/components/TextComponent";
import { useNavigate } from "react-router-dom";
import { hasUsableDiscoveryLocation } from "../../../../core/utilities/locationStorage";

export const NoProfiles = ({
	handleContinue,
	handleSecondLook,
	handleContinueFarther,
	handleCheckNew,
	handleStartAgain,
	handleBack,
	canGoBack,
	canContinueFarther,
	choicesEnabled = true,
	isContinue,
	checkedForNewProfiles,
	choiceMessage,
	permissionState,
	isCrossCountrySearch,
	searchCountry,
	searchAges,
	searchRadius,
	searchDistanceLabel,
	widerSearch = false,
	revisitResult = false,
	moreAvailable = false,
	searchFailed = false,
	handleRetry,
	restartIncludesOtherCountries = false,
}: {
	handleContinue: () => void;
	handleSecondLook: () => void;
	handleContinueFarther: () => void;
	handleCheckNew: () => void;
	handleStartAgain: () => void;
	handleBack: () => void;
	canGoBack: boolean;
	canContinueFarther: boolean;
	choicesEnabled?: boolean;
	isContinue: boolean;
	checkedForNewProfiles: boolean;
	choiceMessage?: string;
	permissionState: string | null;
	isCrossCountrySearch?: boolean;
	searchCountry?: string;
	searchAges?: number[];
	searchRadius?: number;
	searchDistanceLabel?: string;
	widerSearch?: boolean;
	revisitResult?: boolean;
	moreAvailable?: boolean;
	searchFailed?: boolean;
	handleRetry?: () => void;
	restartIncludesOtherCountries?: boolean;
}) => {
	const navigate = useNavigate();
	const evaluateContinue = isContinue;
	const evaluateLocation = hasUsableDiscoveryLocation();
	const showDiscoveryChoices =
		!isCrossCountrySearch && !evaluateContinue && evaluateLocation;

	const handleOpenSettings = () => {
		navigate("settings");
	};

	const handleLocation = () => navigate("/settings/location");

	

	const crossCountryMode = !!isCrossCountrySearch;
	const message = searchFailed
		? "We couldn't load profiles."
		: revisitResult
		? choiceMessage === "There are no more crossed out members to review."
			? "You've reached the end of eligible crossed profiles."
			: "You've finished reviewing these crossed profiles."
		: moreAvailable
		? "You've finished this batch of profiles."
		: crossCountryMode
		? "Unable to find more people"
		: evaluateContinue
		? "Unable to find more people"
		: evaluateLocation
		? widerSearch
			? "You've reached the end of available profiles in this wider search."
			: "You've reached the end of profiles matching your current settings."
		: "Choose a search location";
	const detailsMessage = crossCountryMode
		? "There are no profiles in this selected country right now.\n\nTry another Discovery location."
		: evaluateContinue
		? "Go to Setting > Discovery settings > Change Location"
		: evaluateLocation
		? "Please note that the moment new members join within your search settings they will always display when you next open the app."
		: "Choose a town manually to find profiles nearby. Device location access is optional.";
	const buttonText = crossCountryMode
		? "Open settings"
		: evaluateContinue
		? "Open settings"
		: evaluateLocation
		? "Refresh"
		: "Choose location";
	const buttonAction = crossCountryMode
		? handleOpenSettings
		: evaluateContinue
		? handleOpenSettings
		: evaluateLocation
		? handleContinue
		: handleLocation;
	const Choice = ({
		title,
		explanation,
		onClick,
		primary = false,
		disabled = false,
	}: {
		title: string;
		explanation: string;
		onClick: () => void;
		primary?: boolean;
		disabled?: boolean;
	}) => (
		<div className="w-full flex flex-col gap-2">
			<CustomButton color={primary ? undefined : "white"} onClick={onClick} disabled={disabled}>
				{title}
			</CustomButton>
			<p className="text-sm text-gray-500 text-center leading-5">
				{explanation}
			</p>
		</div>
	);

	return (
		<Column className="bg-white rounded-2xl h-full w-full sm:w-[30rem] p-8 gap-6 overflow-y-auto">
			<div className="w-full flex justify-center pt-6 sm:py-4">
				<img
					src="/hookup4u-Logo-BP.png"
					alt="unjabbed-logo"
					className="w-60"
					width={632}
					height={192}
				/>
			</div>
			<TextComponent
				variant="h2"
				className="font-bold text-primary text-center"
			>
				{message}
			</TextComponent>
			{showDiscoveryChoices && revisitResult && !searchFailed && (
				<p className="text-gray-500 text-center text-sm leading-6">Revisit uses your age range and safety checks, regardless of country or distance. Profiles you Like or Cross again while revisiting are removed from Revisit.</p>
			)}
			{showDiscoveryChoices && !revisitResult && searchRadius && (
				<div className="text-gray-500 text-center text-sm leading-6">
					<p>{widerSearch ? "Wider search: includes other countries" : `Your search: ${searchCountry || "around your search location"}`}
						{searchAges?.length === 2 ? ` · ages ${searchAges[0]}–${searchAges[1]}` : ""}
						{` · within ${searchDistanceLabel || `${searchRadius} km`} of your saved profile location.`}</p>
					<p className="mt-2">Profiles you've already Liked or Crossed are excluded from new results. Revisit shows only eligible crossed profiles.</p>
					{widerSearch && <p className="mt-2">Your saved search settings have not changed.</p>}
					<button className="text-primary underline mt-2" onClick={handleOpenSettings}>Edit search settings</button>
				</div>
			)}
			<Container className="text-gray-500 text-center">
				{showDiscoveryChoices
					? choiceMessage
						? choiceMessage
						: revisitResult
						? "Choose what you would like to do next."
						: checkedForNewProfiles
						? "There are no new profiles within your current settings. Profiles you Cross remain saved under Revisit Crossed Profiles."
						: "Choose what you would like to do next. Profiles you Cross remain saved under Revisit Crossed Profiles."
					: detailsMessage.split("\n").map((line, index) => (
						<span key={index}>
							{line}
							<br />
						</span>
					))}
			</Container>
			{searchFailed && handleRetry && <CustomButton onClick={handleRetry}>Try again</CustomButton>}
			{moreAvailable && !searchFailed && <p className="text-gray-500 text-center">Choose Continue Farther to load the next batch.</p>}
			{showDiscoveryChoices ? (
				<div className="w-full flex flex-col gap-5">
					<Choice
						title="Revisit Crossed Profiles"
						explanation="Simply viewing a crossed profile keeps it in Revisit and returns it to Discovery. If you Cross it again while revisiting, it will no longer appear in Discovery or Revisit."
						onClick={handleSecondLook}
						primary
						disabled={!choicesEnabled}
					/>
					<Choice
						title="Continue Farther"
						explanation="View up to 20 new profiles farther away, including other countries. Your age range and previous choices still apply. Your saved search settings will not change."
						onClick={handleContinueFarther}
						disabled={!choicesEnabled || !canContinueFarther}
					/>
					{canGoBack && (
						<Choice
								title="Back to Previous Profile"
								explanation="Start with your most recently viewed profile and move backwards. Cross and Like work normally."
							onClick={handleBack}
							disabled={!choicesEnabled}
						/>
					)}
					<Choice
						title="Check for New Profiles"
						explanation="Return to your saved search area and distance to check for new profiles. Profiles already viewed in this session will not repeat."
						onClick={handleCheckNew}
						disabled={!choicesEnabled}
					/>
					<Choice
						title="Start Discovery Again"
						explanation={restartIncludesOtherCountries
							? "Restart profiles from the beginning, including other countries within the wider area you previously explored. Your age range and previous choices still apply. Use Revisit Crossed Profiles to review eligible Crosses."
							: "Restart available profiles from the beginning. Existing Crosses stay protected; use Revisit Crossed Profiles to see them again."}
						onClick={handleStartAgain}
						disabled={!choicesEnabled}
					/>
				</div>
			) : (
				<CustomButton onClick={buttonAction}>{buttonText}</CustomButton>
			)}
		</Column>
	);
};
